import React, { useState, useEffect, useCallback } from "react";
import { makeStyles, Paper, Typography, Modal, IconButton } from "@material-ui/core";
import { PlayArrow as PlayArrowIcon, Close as CloseIcon, VideoLibrary as VideoLibraryIcon } from "@material-ui/icons";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import { i18n } from "../../translate/i18n";
import useHelps from "../../hooks/useHelps";

const useStyles = makeStyles(theme => ({
  container: {
    padding: "24px",
    background: "#f5f7fa",
    minHeight: "100vh",
  },
  header: {
    marginBottom: "32px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "16px",
  },
  title: {
    fontSize: "28px",
    fontWeight: 700,
    color: "#1a1a1a",
    margin: 0,
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  subtitle: {
    fontSize: "14px",
    color: "#666",
    marginTop: "4px",
  },
  searchContainer: {
    background: "white",
    borderRadius: "12px",
    padding: "20px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    border: "1px solid #e8ecf1",
    marginBottom: "24px",
  },
  searchInput: {
    width: "100%",
    padding: "12px 16px",
    border: "1px solid #e8ecf1",
    borderRadius: "8px",
    fontSize: "14px",
    fontFamily: "inherit",
    background: "#fafbfc",
    transition: "all 0.2s ease",
    "&:focus": {
      outline: "none",
      borderColor: "#3b82f6",
      background: "white",
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
    },
  },
  mainPaperContainer: {
    overflowY: 'auto',
    maxHeight: 'calc(100vh - 280px)',
  },
  mainPaper: {
    width: '100%',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: "24px",
    padding: "4px",
  },
  helpPaper: {
    position: 'relative',
    width: '100%',
    background: "white",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    border: "1px solid #e8ecf1",
    overflow: "hidden",
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    transition: 'all 0.3s ease',
    "&:hover": {
      transform: 'translateY(-4px)',
      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
      borderColor: theme.palette.primary.main,
    },
  },
  videoThumbnailContainer: {
    position: "relative",
    width: "100%",
    paddingTop: "56.25%",
    background: "#f0f0f0",
    overflow: "hidden",
  },
  videoThumbnail: {
    position: "absolute",
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  playOverlay: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "64px",
    height: "64px",
    background: "rgba(0, 0, 0, 0.7)",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.3s ease",
    "&:hover": {
      background: theme.palette.primary.main,
      transform: "translate(-50%, -50%) scale(1.1)",
    },
  },
  playIcon: {
    color: "white",
    fontSize: "32px",
    marginLeft: "4px",
  },
  videoContent: {
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    flex: 1,
  },
  videoTitle: {
    fontSize: "16px",
    fontWeight: 600,
    color: "#1a1a1a",
    margin: 0,
    lineHeight: "1.4",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  videoDescription: {
    fontSize: "13px",
    color: "#666",
    lineHeight: "1.5",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  videoBadge: {
    position: "absolute",
    top: "12px",
    right: "12px",
    padding: "4px 12px",
    background: "rgba(0, 0, 0, 0.7)",
    color: "white",
    borderRadius: "6px",
    fontSize: "11px",
    fontWeight: 600,
    textTransform: "uppercase",
    backdropFilter: "blur(4px)",
  },
  emptyState: {
    textAlign: "center",
    padding: "80px 20px",
    color: "#999",
    gridColumn: "1 / -1",
  },
  emptyStateIcon: {
    fontSize: "64px",
    color: "#ddd",
    marginBottom: "16px",
  },
  videoModal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: "20px",
  },
  videoModalContent: {
    outline: 'none',
    width: '90%',
    maxWidth: 1280,
    aspectRatio: '16/9',
    position: 'relative',
    backgroundColor: 'black',
    borderRadius: "12px",
    overflow: 'hidden',
    boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
  },
  driveWarning: {
    position: "absolute",
    bottom: "16px",
    left: "50%",
    transform: "translateX(-50%)",
    background: "rgba(239, 68, 68, 0.95)",
    color: "white",
    padding: "12px 20px",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: 600,
    zIndex: 10,
    maxWidth: "90%",
    textAlign: "center",
    backdropFilter: "blur(4px)",
  },
  closeModalButton: {
    position: "absolute",
    top: "16px",
    right: "16px",
    zIndex: 10,
    background: "rgba(0, 0, 0, 0.7)",
    color: "white",
    border: "none",
    borderRadius: "50%",
    width: "40px",
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      background: "rgba(0, 0, 0, 0.9)",
      transform: "scale(1.1)",
    },
  },
}));

const Helps = () => {
  const classes = useStyles();
  const [records, setRecords] = useState([]);
  const { list } = useHelps();
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDriveWarning, setShowDriveWarning] = useState(false);

  useEffect(() => {
    async function fetchData() {
      const helps = await list();
      setRecords(helps);
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openVideoModal = (record) => {
    setSelectedVideo(record);
    if (record.videoType === "drive") {
      setShowDriveWarning(true);
      setTimeout(() => setShowDriveWarning(false), 5000);
    }
  };

  const closeVideoModal = () => {
    setSelectedVideo(null);
    setShowDriveWarning(false);
  };

  const handleModalClose = useCallback((event) => {
    if (event.key === "Escape") {
      closeVideoModal();
    }
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", handleModalClose);
    return () => {
      document.removeEventListener("keydown", handleModalClose);
    };
  }, [handleModalClose]);

  const filteredRecords = records.filter((record) =>
    record.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (record.description && record.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const renderVideoModal = () => {
    if (!selectedVideo) return null;
    
    const videoType = selectedVideo.videoType || "youtube";
    
    return (
      <Modal
        open={Boolean(selectedVideo)}
        onClose={closeVideoModal}
        className={classes.videoModal}
      >
        <div className={classes.videoModalContent}>
          <button
            className={classes.closeModalButton}
            onClick={closeVideoModal}
            title="Fechar (ESC)"
          >
            <CloseIcon />
          </button>
          {videoType === "drive" && (
            <>
              <button
                className={classes.driveOpenButton}
                onClick={() => window.open(`https://drive.google.com/file/d/${selectedVideo.video}/view`, '_blank')}
                title="Abrir no Google Drive"
              >
                🔗 Abrir no Google Drive
              </button>
            </>
          )}
          <iframe
            style={{ width: "100%", height: "100%", position: "absolute", top: 0, left: 0 }}
            src={
              videoType === "drive"
                ? `https://drive.google.com/file/d/${selectedVideo.video}/preview`
                : `https://www.youtube.com/embed/${selectedVideo.video}`
            }
            title={videoType === "drive" ? "Google Drive video player" : "YouTube video player"}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </Modal>
    );
  };

  const renderHelps = () => {
    return (
      <div className={`${classes.mainPaper} ${classes.mainPaperContainer}`}>
        {filteredRecords.length ? filteredRecords.map((record, key) => (
          <Paper key={key} className={classes.helpPaper} onClick={() => openVideoModal(record)}>
            <div className={classes.videoThumbnailContainer}>
              <img
                src={
                  record.videoType === "drive" 
                    ? `https://drive.google.com/thumbnail?id=${record.video}&sz=w400`
                    : `https://img.youtube.com/vi/${record.video}/mqdefault.jpg`
                }
                alt={record.title}
                className={classes.videoThumbnail}
                onError={(e) => {
                  e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='320' height='180' viewBox='0 0 320 180'%3E%3Crect fill='%23f0f0f0' width='320' height='180'/%3E%3Ctext fill='%23999' font-family='sans-serif' font-size='14' x='50%25' y='50%25' text-anchor='middle' dominant-baseline='middle'%3ESem thumbnail%3C/text%3E%3C/svg%3E";
                }}
              />
              <div className={classes.playOverlay}>
                <PlayArrowIcon className={classes.playIcon} />
              </div>
              <span className={classes.videoBadge}>
                {record.videoType === "drive" ? "Google Drive" : "YouTube"}
              </span>
            </div>
            <div className={classes.videoContent}>
              <h3 className={classes.videoTitle}>{record.title}</h3>
              {record.description && (
                <p className={classes.videoDescription}>{record.description}</p>
              )}
            </div>
          </Paper>
        )) : (
          <div className={classes.emptyState}>
            <VideoLibraryIcon className={classes.emptyStateIcon} />
            <p>{searchTerm ? "Nenhum vídeo encontrado com sua busca" : "Nenhum vídeo de ajuda disponível"}</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <MainContainer>
      <div className={classes.container}>
        <div className={classes.header}>
          <div>
            <h1 className={classes.title}>
              <VideoLibraryIcon style={{ fontSize: "32px" }} />
              {i18n.t("helps.title")}
            </h1>
            <p className={classes.subtitle}>
              {filteredRecords.length} {filteredRecords.length === 1 ? "vídeo disponível" : "vídeos disponíveis"}
            </p>
          </div>
        </div>

        <div className={classes.searchContainer}>
          <input
            type="text"
            placeholder="🔍 Buscar vídeos de ajuda..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={classes.searchInput}
          />
        </div>

        {renderHelps()}
        {renderVideoModal()}
      </div>
    </MainContainer>
  );
};

export default Helps;